import { useCallback, useEffect, useRef, useState } from 'react'
import { Product } from '../../types/product'
import ProductItem from '../table/ProductItem'

import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ThreeDots } from 'react-loader-spinner'
import { useFormContext } from '../../context/form'
import './ProductsTable.scss'

const ProductsTable = () => {
  const theme = useTheme()

  const [visibleProducts, setVisibleProducts] = useState<Product[]>([])
  const [startIndex, setStartIndex] = useState<number>(0)
  const productsPerPage: number = 10
  const containerRef = useRef<HTMLDivElement>(null)

  const isMatchDownMd = useMediaQuery(theme.breakpoints.down('md'))

  const TABLE_HEADERS = [
    'Lender',
    'Initial Rate',
    'Type and Duration',
    //@ts-ignore
    theme?.custom?.appConfig?.showMonthlyPayment === 'Yes'
      ? 'Monthly Payment'
      : null,
    'Max LTV',
    'APRC and Fees',
    'Enquire',
  ].filter(Boolean)

  const {
    productsList,
    isFetchingLenders,
    isFetchingProducts,
    isFetchingProductsSuccess,
  } = useFormContext()

  const updateVisibleProducts = useCallback(
    (firstPage = false) => {
      if (containerRef.current) {
        if (firstPage) {
          setStartIndex(0)
        }
        const container = containerRef.current
        const containerBottom = container.scrollTop + container.clientHeight
        const containerScrollHeight = container.scrollHeight

        if (containerBottom >= containerScrollHeight) {
          const endIndex = startIndex + productsPerPage
          setVisibleProducts((prevVisibleProducts) => [
            ...prevVisibleProducts,
            ...productsList.slice(startIndex, endIndex),
          ])
          setStartIndex((prevIndex) => prevIndex + productsPerPage)
        }
      }
    },
    [productsList, startIndex]
  )

  const handleScroll = () => {
    updateVisibleProducts()
  }

  useEffect(() => {
    if (productsList.length) {
      setVisibleProducts(productsList.slice(0, productsPerPage))
    } else {
      setVisibleProducts([])
    }
  }, [productsList])

  useEffect(() => {
    updateVisibleProducts()
  }, [])

  useEffect(() => {
    if (isFetchingProductsSuccess) setStartIndex(productsPerPage)
  }, [isFetchingProductsSuccess])

  return (
    <div
      ref={containerRef}
      style={{ overflowY: 'scroll' }}
      className="hide-scrollbar"
    >
      <table
        role="table"
        summary={`This table shows the best deals on the mortgage market`}
        className="best-buy-slide-table"
      >
        <thead>
          <tr role="row">
            {TABLE_HEADERS.map((header) => (
              <th
                role="columnheader"
                scope="col"
                key={header}
                style={{
                  width: `${(1 / TABLE_HEADERS.length) * 100}%`,
                }}
              >
                <Typography
                  color="primary"
                  component="p"
                  fontSize={{
                    md: '14px !important',
                    xs: '12px !important',
                  }}
                >
                  <b>{header}</b>
                </Typography>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ height: '700px', overflowY: 'scroll' }}>
          {isFetchingLenders || isFetchingProducts ? (
            <tr role="row">
              <td
                role="cell"
                colSpan={isMatchDownMd ? 1 : TABLE_HEADERS.length}
              >
                <Box
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <ThreeDots
                    visible={true}
                    height={80}
                    width={80}
                    //@ts-ignore
                    color={theme?.custom?.customization?.primaryColor!}
                    radius={10}
                  />
                  <Typography
                    color="primary"
                    textAlign={'center'}
                    fontSize={{
                      md: '14px !important',
                      xs: '12px !important',
                    }}
                  >
                    We're getting your products...
                  </Typography>
                </Box>
              </td>
            </tr>
          ) : (
            visibleProducts.map((product, index) => (
              <ProductItem
                key={product.ProductCode}
                product={product}
                isEven={index % 2 === 0}
              />
            ))
          )}
          {visibleProducts.length < productsList.length && (
            <tr role="row">
              <td colSpan={TABLE_HEADERS.length}>
                <Button
                  variant="contained"
                  color="secondary"
                  disableRipple
                  disableElevation
                  onClick={handleScroll}
                >
                  <Typography
                    variant="body1"
                    component="span"
                    color="white"
                    textTransform="capitalize"
                    fontSize={{
                      md: '16px !important',
                      xs: '12px !important',
                    }}
                    sx={{
                      mb: '0 !important',
                    }}
                  >
                    Load More
                  </Typography>
                </Button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default ProductsTable
